<template>
  <div v-if="needInstructions">
    <Stack v-if="showInstructions" class="component is-expanded">
      <div>
        <p class="toggle" @click="$emit('onHideInstructions')">
          Hide instructions
        </p>
        <Title class="first" size="5">Complete your setup:</Title>
        <p class="second">Make the most of our Meeting Assistant by integrating your external tools.</p>
      </div>

      <div v-if="hasCalendarConnected" class="option is-connected">
        <div class="body">
          <header class="header">
            <p class="count">
              <Icon icon="fas fa-check" />
            </p>
            <p class="name">Sync your Calendar</p>
          </header>
        </div>

        <Badge status="success"> Connected </Badge>
      </div>

      <div v-else class="option">
        <div class="body">
          <header class="header">
            <p class="count">1</p>
            <p class="name">Sync your Calendar</p>
          </header>

          <div class="list">
            <span>Import calendar events from</span>
            <span class="vendor">
              <VendorLogo class="vendor-logo" vendor="Google Calendar" />
              Google Calendar
            </span>
            <span>or</span>
            <span class="vendor">
              <VendorLogo class="vendor-logo" vendor="Outlook" />
              Outlook
            </span>
          </div>
        </div>

        <Button iconBefore="fas fa-calendar" @click="showModal = true">
          Connect your Calendar
        </Button>
      </div>

      <div v-if="hasCrmConnected" class="option is-connected">
        <div class="body">
          <header class="header">
            <p class="count">
              <Icon icon="fas fa-check" />
            </p>
            <p class="name">Sync your CRM</p>
          </header>
        </div>

        <Badge status="success"> Connected </Badge>
      </div>

      <div v-else class="option">
        <div class="body">
          <header class="header">
            <p class="count">2</p>
            <p class="name">Sync your CRM</p>
          </header>

          <div class="list">
            <span>Synchronize contacts and task from</span>
            <span class="vendor">
              <VendorLogo class="vendor-logo" vendor="Redtail Icon" />
              Redtail
            </span>
            <span class="vendor">
              <VendorLogo class="vendor-logo" vendor="Wealthbox Icon" />
              Wealthbox
            </span>
            <span class="vendor">
              <VendorLogo
                class="vendor-logo is-salesforce"
                vendor="Salesforce"
              />
              Salesforce
            </span>
            <span>or</span>
            <span class="vendor">
              <VendorLogo class="vendor-logo" vendor="Practifi" />
              Practifi
            </span>
          </div>
        </div>

        <Button iconBefore="fas fa-users" @click="showModal = true">
          Connect your CRM
        </Button>
      </div>
    </Stack>

    <StakeModal v-if="showInstructions" v-model="showModal" noMaxWidth>
      <div class="dashboard">
        <OAuthIntegrationDashboard />
      </div>
    </StakeModal>
  </div>
</template>

<script setup>
import { defineEmits, ref } from "vue";

import { Badge, Button, Icon, Stack, StakeModal, Title } from "@stakeui";
import VendorLogo from "../../oauth/components/VendorLogo.vue";
import OAuthIntegrationDashboard from "../../oauth/views/OAuthIntegrationDashboard.vue";

defineProps({
  showInstructions: {
    type: Boolean,
    required: true,
  },
  hasCrmConnected: Boolean,
  hasCalendarConnected: Boolean,
  needInstructions: Boolean,
});

defineEmits(["onHideInstructions"]);

const showModal = ref(false);
</script>

<style scoped>
.component {
  --color: #577eff;
  background-color: #577eff26;
  border-radius: 1rem;
  color: var(--color);
  padding: 2rem 2.5rem;
}

.second {
  font-weight: 600;
}

.option {
  border: 1px dashed var(--color);
  border-radius: 1rem;
  padding: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.header,
.list,
.vendor,
.count {
  display: flex;
  align-items: center;
  gap: 0.5em;
  justify-content: start;
}

.count {
  border: 1px solid var(--color);
  border-radius: 9999px;
  justify-content: center;
  height: 1.5em;
  width: 1.5em;
}

.name {
  color: var(--stk-text-strong);
  font-weight: 600;
  line-height: 1;
  font-size: 1.125em;
}

.list {
  flex-wrap: wrap;
  padding-left: 2em;
}

.vendor {
  color: var(--stk-text-strong);
}

.vendor-logo {
  height: 1.25em;
  width: 1.25em;

  &.is-salesforce {
    width: 2em;
  }
}

.option.is-connected {
  border-color: var(--stk-success-50);

  .count {
    color: var(--stk-success-50);
    border-width: 0;
  }

  .name {
    opacity: 0.3;
    text-decoration: line-through;
  }
}

.toggle {
  cursor: pointer;

  &:hover {
    text-decoration: underline;
  }
}

.component.is-expanded {
  .toggle {
    float: right;
  }
}

.component.is-collapsed {
  display: flex;
  cursor: pointer;
  border-radius: 0.5rem;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem 1rem;

  .name {
    font-size: 1em;
  }

  &:hover .toggle {
    text-decoration: underline;
  }
}

.dashboard {
  width: 60em;
}
</style>
